import dayjs from "dayjs"
import { useCallback, useEffect } from "react"
import { create } from "zustand"
import { useShallow } from "zustand/react/shallow"

import { useAgreementLinksQuery } from "../../../networking/agreementLinks/queries"
import { useGetCoachSubscriptionQuery } from "../../../networking/coaching/queries"
import {
  useSessionBillingPeriodsQuery,
  useSessionBookingsQuery,
} from "../../../networking/sessions/queries"
import { CoachOverlayType, SubscriptionStatus } from "../../../types"
import { useRouter } from "../../../util/router"
import { localStorageItems } from "../../../util/storage"
import { getMondayOfWeek } from "../../../util/util"
import { CoachTab } from "../CoachTabNav"
import useCoachRoutineQuests from "../routine/useCoachRoutineQuests"

interface CoachOverlayStore {
  currentOverlay: CoachOverlayType | null
  setCurrentOverlay: (currentOverlay: CoachOverlayType | null) => void

  isOpenSessionBooking: boolean
  setIsOpenSessionBooking: (isOpenSessionBooking: boolean) => void
}

export const useCoachOverlayStore = create<CoachOverlayStore>((set) => ({
  currentOverlay: null,
  setCurrentOverlay: (currentOverlay) => set({ currentOverlay }),

  isOpenSessionBooking: true,
  setIsOpenSessionBooking: (isOpenSessionBooking) =>
    set({ isOpenSessionBooking }),
}))

const useCoachOverlay = () => {
  const { data: sessions } = useSessionBookingsQuery()
  const { data: billingPeriods } = useSessionBillingPeriodsQuery()
  const { coachRoutineQuests } = useCoachRoutineQuests()
  const router = useRouter()

  const { isOpenSessionBooking, setCurrentOverlay } = useCoachOverlayStore(
    useShallow((state) => ({
      isOpenSessionBooking: state.isOpenSessionBooking,
      setCurrentOverlay: state.setCurrentOverlay,
    }))
  )
  const { data: coachSubscriptionData, isLoading: isLoadingSubscription } =
    useGetCoachSubscriptionQuery()
  const { data: agreementLinks, isLoading: isLoadingAgreementLinks } =
    useAgreementLinksQuery()
  const hasExpiredSubscription =
    coachSubscriptionData?.status === SubscriptionStatus.EXPIRED &&
    !isLoadingSubscription
  const hasPausedSubscription =
    !!coachSubscriptionData?.pause_collection_end_datetime &&
    !isLoadingSubscription
  const needsToSign =
    !isLoadingAgreementLinks && agreementLinks && agreementLinks.length > 0
  const lastSeenNewRoutinesOverlay = localStorage.getItem(
    localStorageItems.lastSeenNewRoutinesOverlay
  )
  const mondayOfWeek = getMondayOfWeek()
  const isBeforeMonday = (date: string) => dayjs(date).isBefore(mondayOfWeek)
  const hasAnyCoachQuests = coachRoutineQuests.length > 0
  const shouldSeeNewRoutinesOverlayThisWeek =
    hasAnyCoachQuests &&
    (!lastSeenNewRoutinesOverlay || isBeforeMonday(lastSeenNewRoutinesOverlay))

  // Helper function to determine if session booking overlay should be shown
  const shouldShowSessionBookingOverlay = useCallback(() => {
    const lastSeenSessionBookingOverlay = localStorage.getItem(
      localStorageItems.lastSeenSessionBookingOverlay
    )
    const seenInPastDay = lastSeenSessionBookingOverlay
      ? dayjs(lastSeenSessionBookingOverlay).isAfter(dayjs().subtract(1, "day"))
      : false
    const nextSession = billingPeriods?.find((period) =>
      period.bookings.some(
        (booking) =>
          booking.end_datetime && new Date(booking.end_datetime) > new Date()
      )
    )
    const isTextOnly = coachSubscriptionData?.num_sessions === 0
    return !nextSession && !seenInPastDay && !isTextOnly
  }, [billingPeriods, coachSubscriptionData])

  useEffect(() => {
    const showFailedPayment = hasExpiredSubscription
    const showPausedSubscription = hasPausedSubscription
    const showFTUX =
      sessions &&
      sessions.length === 0 &&
      billingPeriods &&
      billingPeriods.length > 0
    const showTOS = needsToSign && !isLoadingSubscription
    const showSessionBooking =
      shouldShowSessionBookingOverlay() && isOpenSessionBooking
    const showNewRoutines =
      shouldSeeNewRoutinesOverlayThisWeek &&
      router.query.coachTab === CoachTab.ROUTINE

    if (showFailedPayment) {
      setCurrentOverlay(CoachOverlayType.EXPIRED_SUBSCRIPTION)
    } else if (showPausedSubscription) {
      setCurrentOverlay(CoachOverlayType.PAUSED_SUBSCRIPTION)
    } else if (showFTUX) {
      setCurrentOverlay(CoachOverlayType.FTUX)
    } else if (showTOS) {
      setCurrentOverlay(CoachOverlayType.TOS)
    } else if (showSessionBooking) {
      setCurrentOverlay(CoachOverlayType.SESSION_BOOKING)
    } else if (showNewRoutines) {
      setCurrentOverlay(CoachOverlayType.NEW_ROUTINES)
    } else {
      setCurrentOverlay(null)
    }
  }, [
    sessions,
    billingPeriods,
    hasPausedSubscription,
    setCurrentOverlay,
    isOpenSessionBooking,
    isLoadingSubscription,
    hasExpiredSubscription,
    needsToSign,
    shouldShowSessionBookingOverlay,
    shouldSeeNewRoutinesOverlayThisWeek,
    router,
  ])
}

export default useCoachOverlay
